import React, { useState, useEffect } from 'react';
import { Card, Row, Col, Statistic, Button, Space, Typography, Divider } from 'antd';
import { 
  GiftOutlined, 
  UserOutlined, 
  CalendarOutlined, 
  CheckCircleOutlined,
  PlusOutlined,
  ReloadOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { giftService } from '../../../services/giftService';
import styles from './GiftsDashboardPage.module.css';

const { Title, Text } = Typography;

interface GiftStats {
  total: {
    amount: number;
    count: number;
  };
  byEvent: Array<{
    event_name: string;
    event_date: string;
    event_type: string;
    total_amount: number;
    gift_count: number;
  }>;
  byRelationship: Array<{
    relationship: string;
    total_amount: number;
    gift_count: number;
  }>;
  notReturnedCount: number;
}

const GiftsDashboardPage: React.FC = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState<GiftStats | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchStats = async () => {
    try {
      setLoading(true);
      const response = await giftService.getStats();
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStats();
  }, []);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('vi-VN', {
      style: 'currency',
      currency: 'VND'
    }).format(amount);
  };

  const getRelationshipLabel = (relationship: string) => {
    const labels: Record<string, string> = {
      family_internal: 'Gia đình nội',
      family_external: 'Gia đình ngoại',
      friend_bride: 'Bạn cô dâu',
      friend_groom: 'Bạn chú rể',
      colleague: 'Đồng nghiệp',
      business_partner: 'Đối tác',
      other: 'Khác'
    };
    return labels[relationship] || relationship;
  };

  const getEventTypeLabel = (eventType: string) => {
    const labels: Record<string, string> = {
      wedding: 'Đám cưới',
      housewarming: 'Tân gia',
      birthday: 'Sinh nhật',
      graduation: 'Tốt nghiệp',
      other: 'Khác'
    };
    return labels[eventType] || eventType;
  };

  if (loading) {
    return (
      <div className={styles.loadingContainer}>
        <div className={styles.loadingSpinner}></div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <Title level={2} className={styles.title}>
          Dashboard
        </Title>
        <Space>
          <Button 
            icon={<ReloadOutlined />} 
            onClick={fetchStats}
            loading={loading}
          >
            Làm mới
          </Button>
          <Button 
            type="primary" 
            icon={<PlusOutlined />}
            onClick={() => navigate('/gifts/add')}
          >
            Thêm tiền mừng
          </Button>
        </Space>
      </div>

      {/* Thống kê tổng quan */}
      <Row gutter={[16, 16]} className={styles.statsRow}>
        <Col xs={24} sm={12} lg={6}>
          <Card className={styles.statCard}>
            <Statistic
              title="Tổng tiền mừng"
              value={stats?.total.amount || 0}
              precision={0}
              valueStyle={{ color: '#3f8600' }}
              prefix="₫"
              suffix={
                <Text type="secondary" style={{ fontSize: '14px' }}>
                  ({stats?.total.count || 0} món)
                </Text>
              }
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card className={styles.statCard}>
            <Statistic
              title="Sự kiện"
              value={stats?.byEvent.length || 0}
              prefix={<CalendarOutlined />}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card className={styles.statCard}>
            <Statistic
              title="Người mừng"
              value={stats?.byRelationship.reduce((sum, item) => sum + item.gift_count, 0) || 0}
              prefix={<UserOutlined />}
              valueStyle={{ color: '#722ed1' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card className={styles.statCard}>
            <Statistic
              title="Chưa đáp lễ"
              value={stats?.notReturnedCount || 0}
              prefix={<CheckCircleOutlined />}
              valueStyle={{ color: '#fa8c16' }}
            />
          </Card>
        </Col>
      </Row>

      <Row gutter={[16, 16]}>
        {/* Tiền mừng theo sự kiện */}
        <Col xs={24} lg={12}>
          <Card 
            title="Tiền mừng theo sự kiện" 
            className={styles.eventCard}
            extra={
              <Button type="link" onClick={() => navigate('/gifts/events')}>
                Xem tất cả
              </Button>
            }
          >
            {stats?.byEvent.length ? (
              <div className={styles.eventList}>
                {stats.byEvent.slice(0, 5).map((event, index) => (
                  <div key={index} className={styles.eventItem}>
                    <div className={styles.eventInfo}>
                      <Text strong>{event.event_name}</Text>
                      <Text type="secondary">
                        {new Date(event.event_date).toLocaleDateString('vi-VN')} - {getEventTypeLabel(event.event_type)}
                      </Text>
                    </div>
                    <div className={styles.eventAmount}>
                      <Text strong>{formatCurrency(event.total_amount)}</Text>
                      <Text type="secondary">({event.gift_count} món)</Text>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className={styles.emptyState}>
                <Text type="secondary">Chưa có sự kiện nào</Text>
              </div>
            )}
          </Card>
        </Col>

        {/* Tiền mừng theo mối quan hệ */}
        <Col xs={24} lg={12}>
          <Card 
            title="Tiền mừng theo mối quan hệ" 
            className={styles.relationshipCard}
            extra={
              <Button type="link" onClick={() => navigate('/gifts/givers')}>
                Xem tất cả
              </Button>
            }
          >
            {stats?.byRelationship.length ? (
              <div className={styles.relationshipList}>
                {stats.byRelationship.slice(0, 5).map((item, index) => (
                  <div key={index} className={styles.relationshipItem}>
                    <div className={styles.relationshipInfo}>
                      <Text strong>{getRelationshipLabel(item.relationship)}</Text>
                      <Text type="secondary">{item.gift_count} món</Text>
                    </div>
                    <div className={styles.relationshipAmount}>
                      <Text strong>{formatCurrency(item.total_amount)}</Text>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className={styles.emptyState}>
                <Text type="secondary">Chưa có dữ liệu</Text>
              </div>
            )}
          </Card>
        </Col>
      </Row>

      {/* Các hành động nhanh */}
      <Card title="Hành động nhanh" className={styles.quickActionsCard}>
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={12} md={6}>
            <Button 
              block 
              size="large"
              icon={<PlusOutlined />}
              onClick={() => navigate('/gifts/events/add')}
            >
              Tạo sự kiện
            </Button>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Button 
              block 
              size="large"
              icon={<UserOutlined />}
              onClick={() => navigate('/gifts/givers/add')}
            >
              Thêm người mừng
            </Button>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Button 
              block 
              size="large"
              icon={<GiftOutlined />}
              onClick={() => navigate('/gifts/add')}
            >
              Ghi nhận tiền mừng
            </Button>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Button 
              block 
              size="large"
              icon={<CheckCircleOutlined />}
              onClick={() => navigate('/gifts/return')}
            >
              Đáp lễ
            </Button>
          </Col>
        </Row>
      </Card>
    </div>
  );
};

export default GiftsDashboardPage; 